import React, { useState, useEffect } from 'react';
import apiService, { errorHelpers, formatHelpers, authHelpers } from '../../../utils/apiService';


const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [alert, setAlert] = useState(null);
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // track which user is currently resending
  const [resendingUserId, setResendingUserId] = useState(null);

  const [editFormData, setEditFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    address: '',
    role: ''
  });

  // New: only super_admins or admins editing regular users
  const canEditUser = (user) =>
    currentUser?.role === 'super_admin' ||
    (currentUser?.role === 'admin' && user.role === 'user');

  useEffect(() => {
    loadUsers();
  }, [currentPage, filters]);
  const openCreateModal = () => setShowCreateModal(true);
  const closeCreateModal = () => setShowCreateModal(false);
  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 20,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await apiService.admin.getUsers(params);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load users:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load users'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleToggleUserStatus = async (userId) => {
    try {
      const response = await apiService.admin.toggleUserStatus(userId);
      setAlert({
        type: 'success',
        message: response.data.message
      });
      loadUsers(); // Refresh the list
    } catch (error) {
      const errorMessage = errorHelpers.getErrorMessage(error);
      setAlert({
        type: 'error',
        message: errorMessage
      });
    }
  };

  const handleResendVerificationEmail = async (userId) => {
    try {
      setResendingUserId(userId);
      await apiService.admin.resendVerificationEmail(userId);
      setAlert({ type: 'success', message: 'Verification email resent.' });
    } catch (err) {
      setAlert({ type: 'error', message: errorHelpers.getErrorMessage(err) });
    } finally {
      setResendingUserId(null);
    }
  };



  const handleVerifyUserEmail = async (userId) => {
    try {
      const response = await apiService.admin.verifyUserEmail(userId);
      setAlert({
        type: 'success',
        message: response.data.message
      });
      loadUsers(); // Refresh the list
    } catch (error) {
      const errorMessage = errorHelpers.getErrorMessage(error);
      setAlert({
        type: 'error',
        message: errorMessage
      });
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const response = await apiService.admin.updateUserRole(userId, newRole);
      setAlert({
        type: 'success',
        message: response.data.message
      });
      loadUsers(); // Refresh the list
    } catch (error) {
      const errorMessage = errorHelpers.getErrorMessage(error);
      setAlert({
        type: 'error',
        message: errorMessage
      });
    }
  };

  // Open edit modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditFormData({
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
      phone: user.phone || '',
      address: user.address || '',
      role: user.role
    });
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
  };

  // Save edits
  const handleSaveUser = async () => {
    try {
      // Ensure your apiService.admin.updateUser endpoint exists
      const response = await apiService.admin.updateUser(selectedUser.id, editFormData);
      setAlert({ type: 'success', message: response.data.message || 'User updated successfully' });
      loadUsers();
      closeEditModal();
    } catch (error) {
      setAlert({ type: 'error', message: errorHelpers.getErrorMessage(error) });
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'user': return 'User';
      default: return role;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'badge-error';
      case 'admin': return 'badge-warning';
      case 'user': return 'badge-info';
      default: return 'badge-info';
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // CreateUserModal Component
  // ──────────────────────────────────────────────────────────────────────────
  const CreateUserModal = ({ onClose, currentUser }) => {
    const isSuperAdmin = currentUser.role === 'super_admin';

    // ── Form state ────────────────────────────────────────────
    const [form, setForm] = useState({
      firstname: '',
      middlename: '',
      lastname: '',
      email: '',
      password: '',
      phone: '',
      address: '',
      role: isSuperAdmin ? 'user' : 'user'
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // ── Handle input changes ───────────────────────────────────
    const handleChange = (e) => {
      const { name, value } = e.target;
      setForm(f => ({ ...f, [name]: value }));
    };

    // ── Submit handler ─────────────────────────────────────────
    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setErrors({});

      try {
        await apiService.admin.createUser(form);
        onClose();
      } catch (err) {
        const msg = errorHelpers.getErrorMessage(err);
        setErrors({ submit: msg });
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="modal-backdrop">
        <div className="modal">
          <header className="modal-header">
            <h3>Create New User</h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </header>
          <form onSubmit={handleSubmit} className="modal-body">
            {['firstname', 'middlename', 'lastname', 'email', 'password', 'phone', 'address'].map(field => (
              <div className="form-group" key={field}>
                <label>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                <input
                  type={field === 'password' ? 'password' : 'text'}
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  required={['firstname', 'lastname', 'email', 'password'].includes(field)}
                />
              </div>
            ))}

            {isSuperAdmin && (
              <div className="form-group">
                <label>Role</label>
                <select
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
            )}

            {errors.submit && (
              <div className="form-error">{errors.submit}</div>
            )}

            <footer className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Creating…' : 'Create User'}
              </button>
            </footer>
          </form>
        </div>
      </div>
    );
  };

  const currentUser = authHelpers.getCurrentUser();
  const canUpdateRoles = currentUser?.role === 'super_admin';

  return (
    <div className="user-management">
      <div className="management-header">
        <h2>👥 User Management</h2>
        <p>Manage user accounts, roles, and permissions</p>
        <button
          className="btn btn-primary add-user-btn"
          onClick={openCreateModal}
        >
          + Add User
        </button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type} mb-4`}>
          {alert.message}
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Search Users</label>
            <input
              type="text"
              className="filter-input"
              placeholder="Search by name or email..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Role</label>
            <select
              className="filter-select"
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="user">Users</option>
              <option value="admin">Admins</option>
              <option value="super_admin">Super Admins</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              className="filter-select"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>No Users Found</h3>
            <p>No users match your current filters.</p>
          </div>
        ) : (
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Email Verified</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">
                          {user.firstname.charAt(0)}{user.lastname.charAt(0)}
                        </div>
                        <div className="user-details">
                          <div className="user-name">
                            {user.firstname} {user.lastname}
                          </div>
                          <div className="user-email">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {canUpdateRoles && user.id !== currentUser.id ? (
                        <select
                          className={`role-select ${getRoleBadgeColor(user.role)}`}
                          value={user.role}
                          onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      ) : (
                        <span className={`badge ${getRoleBadgeColor(user.role)}`}>
                          {getRoleDisplayName(user.role)}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${user.is_enabled ? 'enabled' : 'disabled'}`}>
                        {user.is_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td>
                      <div className="verification-status">
                        <span className={`verification-badge ${user.is_verified ? 'verified' : 'unverified'}`}>
                          {user.is_verified ? '✅ Verified' : '⏳ Pending'}
                        </span>
                        {/*!user.is_verified && (
                          <button
                            className="verify-btn"
                            onClick={() => handleVerifyUserEmail(user.id)}
                            title="Manually verify email"
                          >
                            Verify
                          </button>
                        )*/}
                        {!user.is_verified && (
                          <div className="verification-actions">
                            <button
                              className="verify-btn"
                              onClick={() => handleVerifyUserEmail(user.id)}
                              title="Manually verify email"
                              disabled={resendingUserId === user.id}
                            >
                              Verify
                            </button>

                            <button
                              className="verify-btn resend-btn"
                              onClick={() => handleResendVerificationEmail(user.id)}
                              title="Resend verification email"
                              disabled={resendingUserId === user.id}
                            >
                              {resendingUserId === user.id
                                ? <img
                                  src="/images/loading.gif"
                                  alt="Resending…"
                                  className="button-spinner"
                                />
                                : '↻ Resend'}
                            </button>
                          </div>
                        )}


                      </div>
                    </td>
                    <td>
                      <span className="join-date">
                        {formatHelpers.formatDate(user.created_at)}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        {canEditUser(user) && (
                          <button
                            className="action-btn edit"
                            onClick={() => openEditModal(user)}
                            title="Edit user details"
                          >
                            ✏️ Edit
                          </button>
                        )}
                        {user.id !== currentUser.id && user.role !== 'super_admin' && (
                          <button
                            className={`action-btn ${user.is_enabled ? 'disable' : 'enable'}`}
                            onClick={() => handleToggleUserStatus(user.id)}
                            title={user.is_enabled ? 'Disable user' : 'Enable user'}
                          >
                            {user.is_enabled ? '🚫 Disable' : '✅ Enable'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Create User Modal ──────────────────────────────────── */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => {
            closeCreateModal();
            loadUsers();
          }}
          currentUser={authHelpers.getCurrentUser()}
        />
      )}
      {/* Edit User Modal */}
      {isEditModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit User</h3>
              <button className="close-btn" onClick={closeEditModal}>×</button>
            </div>
            <div className="modal-body">
              <label>First Name</label>
              <input
                type="text"
                value={editFormData.firstname}
                onChange={e => setEditFormData({ ...editFormData, firstname: e.target.value })}
              />
              <label>Last Name</label>
              <input
                type="text"
                value={editFormData.lastname}
                onChange={e => setEditFormData({ ...editFormData, lastname: e.target.value })}
              />
              <label>Email</label>
              <input
                type="email"
                value={editFormData.email}
                onChange={e => setEditFormData({ ...editFormData, email: e.target.value })}
              />
              <label>Phone</label>
              <input
                type="text"
                value={editFormData.phone}
                onChange={e => setEditFormData({ ...editFormData, phone: e.target.value })}
              />
              <label>Address</label>
              <textarea
                value={editFormData.address}
                onChange={e => setEditFormData({ ...editFormData, address: e.target.value })}
              />
              {canUpdateRoles && (
                <>
                  <label>Role</label>
                  <select
                    value={editFormData.role}
                    onChange={e => setEditFormData({ ...editFormData, role: e.target.value })}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn cancel" onClick={closeEditModal}>Cancel</button>
              <button className="btn save" onClick={handleSaveUser}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={!pagination.has_prev || loading}
          >
            ← Previous
          </button>

          <div className="pagination-info">
            Page {pagination.page} of {pagination.pages}
            ({pagination.total} total users)
          </div>

          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={!pagination.has_next || loading}
          >
            Next →
          </button>
        </div>
      )}

      <style jsx>{`
        .user-management {
          max-width: 1200px;
        }

        .management-header {
          margin-bottom: 2rem;
        }

        .management-header h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--gray-800);
          margin-bottom: 0.5rem;
        }

        .management-header p {
          color: var(--gray-600);
        }

        .filters-section {
          background: white;
          padding: 1.5rem;
          border-radius: 0.75rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
        }

        .filter-label {
          font-weight: 500;
          color: var(--gray-700);
          margin-bottom: 0.5rem;
        }

        .filter-input,
        .filter-select {
          padding: 0.75rem;
          border: 2px solid var(--gray-200);
          border-radius: 0.5rem;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }

        .filter-input:focus,
        .filter-select:focus {
          outline: none;
          border-color: var(--secondary-blue);
        }

        .table-container {
          background: white;
          border-radius: 0.75rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .loading-state,
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--gray-200);
          border-top: 4px solid var(--secondary-blue);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .empty-state h3 {
          color: var(--gray-700);
          margin-bottom: 0.5rem;
        }

        .empty-state p {
          color: var(--gray-600);
        }

        .users-table-wrapper {
          overflow-x: auto;
        }

        .users-table {
          width: 100%;
          border-collapse: collapse;
        }

        .users-table th,
        .users-table td {
          padding: 1rem;
          text-align: left;
          border-bottom: 1px solid var(--gray-200);
        }

        .users-table th {
          background: var(--gray-50);
          font-weight: 600;
          color: var(--gray-700);
          position: sticky;
          top: 0;
        }

        .users-table tr:hover {
          background: var(--gray-50);
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .user-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .user-details {
          flex: 1;
        }

        .user-name {
          font-weight: 500;
          color: var(--gray-800);
        }

        .user-email {
          font-size: 0.875rem;
          color: var(--gray-600);
        }

        .role-select {
          background: none;
          border: none;
          font-weight: 500;
          font-size: 0.875rem;
          padding: 0.25rem 0.5rem;
          border-radius: 1rem;
          cursor: pointer;
        }

        .role-select.badge-error {
          background: var(--red-100);
          color: var(--error-red);
        }

        .role-select.badge-warning {
          background: var(--yellow-100);
          color: var(--warning-yellow);
        }

        .role-select.badge-info {
          background: var(--blue-100);
          color: var(--secondary-blue);
        }

        .status-badge {
          font-size: 0.875rem;
          font-weight: 500;
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
        }

        .status-badge.enabled {
          background: var(--green-100);
          color: var(--success-green);
        }

        .status-badge.disabled {
          background: var(--red-100);
          color: var(--error-red);
        }

        .verification-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .verification-badge {
          font-size: 0.875rem;
          font-weight: 500;
        }

        .verification-badge.verified {
          color: var(--success-green);
        }

        .verification-badge.unverified {
          color: var(--warning-yellow);
        }

        .verify-btn {
          background: var(--blue-100);
          color: var(--secondary-blue);
          border: none;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .verify-btn:hover {
          background: var(--secondary-blue);
          color: white;
        }

        .join-date {
          font-size: 0.875rem;
          color: var(--gray-600);
        }

        .actions {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          border: none;
          padding: 0.5rem 0.75rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-btn.disable {
          background: var(--red-100);
          color: var(--error-red);
        }

        .action-btn.disable:hover {
          background: var(--error-red);
          color: white;
        }

        .action-btn.enable {
          background: var(--green-100);
          color: var(--success-green);
        }

        .action-btn.enable:hover {
          background: var(--success-green);
          color: white;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-top: 2rem;
          padding: 2rem 0;
        }

        .pagination-btn {
          background: white;
          border: 2px solid var(--gray-300);
          color: var(--gray-700);
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .pagination-btn:hover:not(:disabled) {
          border-color: var(--secondary-blue);
          color: var(--secondary-blue);
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-info {
          font-weight: 500;
          color: var(--gray-700);
          text-align: center;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .filters-grid {
            grid-template-columns: 1fr;
          }

          .users-table {
            font-size: 0.875rem;
          }

          .users-table th,
          .users-table td {
            padding: 0.75rem 0.5rem;
          }

          .user-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .pagination {
            flex-direction: column;
            gap: 0.5rem;
          }

          .pagination-btn {
            width: 100%;
            max-width: 200px;
          }
        }

        /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }
                .modal {
                    background: white;
                    border-radius: 0.75rem;
                    width: 90%;
                    max-width: 500px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    overflow: hidden;
                }
                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem;
                    border-bottom: 1px solid #e5e5e5;
                }
                .close-btn {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                }
                .modal-body {
                    padding: 1rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }
                .modal-body input,
                .modal-body textarea,
                .modal-body select {
                    padding: 0.5rem;
                    border: 1px solid #ccc;
                    border-radius: 0.5rem;
                    width: 100%;
                }
                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.5rem;
                    padding: 1rem;
                    border-top: 1px solid #e5e5e5;
                }
                .btn {
                    padding: 0.5rem 1rem;
                    border-radius: 0.5rem;
                    font-weight: 500;
                    cursor: pointer;
                }
                .btn.cancel {
                    background: #f3f3f3;
                    color: #333;
                }
                .btn.save {
                    background: var(--secondary-blue);
                    color: white;
                }
                .action-btn.edit {
                    background: var(--blue-100);
                    color: var(--secondary-blue);
                }
                .action-btn.edit:hover {
                    background: var(--secondary-blue);
                    color: white;
                }

                .modal-backdrop {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
  .verification-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.button-spinner {
  width: 16px;
  height: 16px;
  vertical-align: middle;
}
.resend-btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}


      `}</style>
    </div>
  );
};

export default UserManagement;